/**
 * Analytics utilities for tracking user interactions and loan applications
 */
import { LoanCycle } from "../helpers/loan_cycle_helper";
import { ANALYTICS_EVENTS } from "../helpers/analytics_events_helper";
import { ProductType } from "../types/product";

export interface CustomDimensions {
  product_type?: ProductType;
  product_amount?: number;
  product_length?: number;
  product_apr?: number;
  product_state?: string;
  application_number?: number;
  potential_first_loan?: number;
  has_open_loan?: boolean;
  is_logged_in?: boolean;
  funded_count?: number;
  entry_point?: string;
  flow_context?: string;
}

export interface UserContext {
  applicationNumber?: number;
  activeLoanCycle?: string;
  fundedCount?: number;
}

export interface ProductContext {
  product?: ProductType;
  amount?: number;
  length?: number;
  apr?: number;
  state?: string;
}

export interface AnalyticsContext {
  entry_point?: string;
  flow_context?: string;
}

// Utility functions
const isAnalyticsEnabled = (): boolean => {
  return (
    process.env.REACT_APP_ANALYTICS_ENABLED === "true" &&
    typeof window !== "undefined"
  );
};

const hasDataLayer = (win: Window): win is Window & { dataLayer: any[] } => {
  return Array.isArray(win.dataLayer);
};

const logEvent = (eventName: string, data: any): void => {
  if (
    process.env.NODE_ENV === "development" ||
    process.env.NODE_ENV === "test"
  ) {
    console.log("📊", eventName, data);
  }
};

const validateEventName = (eventName: string): boolean => {
  const trimmedEventName = eventName && eventName.trim();
  return (
    typeof trimmedEventName === "string" &&
    trimmedEventName.length > 0 &&
    Object.values(ANALYTICS_EVENTS).includes(
      trimmedEventName as keyof typeof ANALYTICS_EVENTS
    )
  );
};

// Core tracking function
export const trackEvent = (
  eventName: string,
  customDimensions: CustomDimensions = {}
): void => {
  if (!validateEventName(eventName)) {
    return;
  }

  const eventData = {
    event: eventName.trim(),
    ...customDimensions,
  };

  // Always log in development/test mode
  logEvent(eventName, customDimensions);

  // Only push to dataLayer if analytics is enabled
  if (isAnalyticsEnabled() && hasDataLayer(window)) {
    try {
      window.dataLayer.push(eventData);
    } catch (error) {
      console.error("Analytics error:", error);
    }
  }
};

// Product context extraction
const extractProductContext = (product: ProductContext = {}) => ({
  product_type:
    product.product && Object.values(ProductType).includes(product.product as ProductType)
      ? (product.product as ProductType)
      : undefined,
  product_amount:
    typeof product.amount === "number" ? product.amount : undefined,
  product_length:
    typeof product.length === "number" ? product.length : undefined,
  product_apr: typeof product.apr === "number" ? product.apr : undefined,
  product_state: typeof product.state === "string" ? product.state : undefined,
});

// User context extraction
const extractUserContext = (user: UserContext = {}) => {
  const hasUser = user && Object.keys(user).length > 0;

  return {
    is_logged_in: hasUser,
    application_number:
      hasUser && typeof user.applicationNumber === "number"
        ? user.applicationNumber + 1
        : undefined,
    has_open_loan:
      hasUser && user.activeLoanCycle
        ? LoanCycle.ACTIVE.includes(user.activeLoanCycle)
        : undefined,
    funded_count:
      hasUser && typeof user.fundedCount === "number"
        ? user.fundedCount
        : undefined,
  };
};

// Analytics context extraction
export const extractAnalyticsContext = (
  analyticsContext: AnalyticsContext = {}
): AnalyticsContext => ({
  entry_point:
    typeof analyticsContext.entry_point === "string"
      ? analyticsContext.entry_point.trim()
      : undefined,
  flow_context:
    typeof analyticsContext.flow_context === "string"
      ? analyticsContext.flow_context.trim()
      : undefined,
});

// Application event tracking
export const trackApplicationEvent = (
  eventName: string,
  productContext: ProductContext,
  userContext: UserContext,
  analyticsContext: AnalyticsContext
): void => {
  if (!validateEventName(eventName)) {
    return;
  }

  trackEvent(eventName, {
    ...extractProductContext(productContext),
    ...extractUserContext(userContext),
    ...extractAnalyticsContext(analyticsContext),
  });
};
